import { useEffect, useState, type RefObject } from 'react';

interface Heading { id: string; text: string; level: number }

/** Inhoudsopgave uit de bovenste drie kopniveaus van het gerenderde artikel,
 *  met IntersectionObserver-scroll-spy (rootMargin -20%/-70%, zoals het
 *  origineel).
 *
 *  Sinds de huisstijlmigratie begint de artikelinhoud bij <h2> — de paginatitel
 *  is de enige <h1>. De bovenste drie niveaus zijn dus h2, h3 en h4. */
export function ArticleToc({ articleRef }: { articleRef: RefObject<HTMLElement | null> }) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    // Alleen koppen ÍN de artikeltekst: de paginatitel is de h1 van de pagina
    // en hoort niet als eerste regel in zijn eigen inhoudsopgave te staan.
    const prose = el.querySelector<HTMLElement>('.kb-prose') ?? el;
    const found: Heading[] = [];
    prose.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6').forEach((h, i) => {
      if (!h.id) h.id = `section-${i + 1}`;
      const text = (h.textContent || '').trim();
      if (!text) return;
      // De tagnaam ís nu het niveau: de renderer degradeert niet meer, dus er
      // is geen data-level meer om op terug te vallen. h1 kan in de inhoud niet
      // meer voorkomen, maar wordt hier als h2 behandeld voor het geval een
      // artikel nog niet gemigreerd is.
      const level = Math.max(2, Number(h.tagName[1]));
      if (level <= 4) found.push({ id: h.id, text, level });
    });
    setHeadings(found);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        }
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );
    for (const h of found) {
      const node = document.getElementById(h.id);
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [articleRef]);

  if (headings.length === 0) {
    return <p className="text-sm text-ink-3">Geen koppen gevonden</p>;
  }

  return (
    <nav aria-label="Inhoudsopgave">
      {/* h2 is het bovenste niveau in de inhoud en springt dus niet in;
          vandaar dat het inspringen vanaf niveau 2 telt. */}
      <ul className="space-y-0.5 text-sm">
        {headings.map((h) => (
          <li key={h.id} style={{ paddingLeft: `${(h.level - 2) * 0.75}rem` }}>
            <a
              href={`#${h.id}`}
              aria-current={activeId === h.id ? 'true' : undefined}
              onClick={(e) => {
                e.preventDefault();
                const node = document.getElementById(h.id);
                if (!node) return;
                // De focus MOET mee naar de kop. Alleen scrollen laat de focus
                // in de inhoudsopgave staan: je springt visueel naar hoofdstuk
                // 7 en Tab gaat daarna gewoon verder bij hoofdstuk 2.
                node.setAttribute('tabindex', '-1');
                node.focus({ preventScroll: true });
                node.scrollIntoView({
                  behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                });
                history.replaceState(null, '', `#${h.id}`);
              }}
              className={`block rounded-sm px-2 py-1 leading-snug transition ${
                activeId === h.id
                  ? 'bg-accent-soft font-medium text-accent-soft-ink'
                  : 'text-ink-3 hover:bg-surface-hover hover:text-ink'
              }`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
